/**
 * JAISIU-3588 — "+ menu" popover for the Fleet Chat composer.
 *
 * Collapses the inline tool row (Palace / Draft / Controls / Mention /
 * Commands) and the Queue mode selector into a single, keyboard-accessible
 * popover triggered by a `+` button next to `Attach`.
 *
 * Layout:
 *
 *   [ Attach ] [ + ]                                   [ Send ]
 *
 * The popover lists five actions plus the Collect/Steer toggle. Each
 * action is rendered as a row with an icon, label, and `›` chevron
 * hinting at deeper submenus (currently only the entries themselves
 * open — the chevron signals future expansion without claiming a
 * non-existent submenu today).
 *
 * Keyboard model:
 * - Esc closes the popover and returns focus to the `+` trigger.
 * - ArrowUp/ArrowDown move focus across rows.
 * - Home/End jump to first/last.
 * - Enter / Space activates the focused row.
 * - Tab cycles focus naturally across rows and exits to the next
 *   composer control on the last row (button group).
 * - Outside click closes the popover.
 *
 * The component is a pure function — it takes props, returns a
 * Lit `TemplateResult`. State (open/closed, focused index) lives on
 * the host view (`ChatProps`) so the popover survives re-renders.
 */

import { html, nothing, type TemplateResult } from "lit";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import { icons } from "../icons.ts";

export type ChatComposerPlusMenuProps = {
  open: boolean;
  /** Index of the currently focused row (-1 = none, used for keyboard nav). */
  focusedIndex: number;
  /** Trigger ref-host for outside click detection + focus return. */
  triggerRef: HTMLElement | null;
  /** Popover panel ref-host for outside click detection. */
  panelRef: HTMLElement | null;

  /** Active session key (so we can route the toggle correctly). */
  sessionKey: string;
  /** Current resolved queue mode (already merged from global + per-session). */
  queueMode: "collect" | "steer";
  /** Toggle between Collect and Steer — calls `props.onQueueModeSelect` on the host. */
  onQueueModeToggle: (next: "collect" | "steer") => void;

  onOpenPalace: () => void;
  onOpenDraft: () => void;
  onOpenControls: () => void;
  onOpenMention: () => void;
  onOpenCommands: () => void;

  onToggle: () => void;
  onClose: () => void;
  onFocusChange: (next: number) => void;
};

/** Identifier for each row — keeps `onKeyDown` switch readable. */
type PlusMenuRow =
  | "palace"
  | "draft"
  | "controls"
  | "mention"
  | "commands"
  | "queue-collect"
  | "queue-steer";

const ROWS: readonly PlusMenuRow[] = [
  "palace",
  "draft",
  "controls",
  "mention",
  "commands",
];

function iconForRow(row: PlusMenuRow): TemplateResult | typeof nothing {
  switch (row) {
    case "palace":
      return icons.prism;
    case "draft":
      return icons.folder;
    case "controls":
      return icons.settings;
    case "mention":
      return icons.userPlus;
    case "commands":
      return icons.command;
    case "queue-collect":
    case "queue-steer":
      return nothing;
  }
}

function rowLabel(row: PlusMenuRow): string {
  switch (row) {
    case "palace":
      return "Palace";
    case "draft":
      return "Draft";
    case "controls":
      return "Controls";
    case "mention":
      return "Mention";
    case "commands":
      return "Commands";
    case "queue-collect":
      return "Collect";
    case "queue-steer":
      return "Steer";
  }
}

function rowTooltip(row: PlusMenuRow): string {
  if (row === "queue-collect") {
    return "Collect — queue messages and run them when the agent is idle.";
  }
  if (row === "queue-steer") {
    return "Steer — run each message as it arrives; interrupt the current run if needed.";
  }
  return `${rowLabel(row)} — ${hintFor(row)}`;
}

function hintFor(row: PlusMenuRow): string {
  switch (row) {
    case "palace":
      return "Quantum Palace session continuity";
    case "draft":
      return "Local draft assistant";
    case "controls":
      return "Send controls (orchestrate / project / self-improve)";
    case "mention":
      return "Mention an agent or attach context";
    case "commands":
      return "Slash commands (/)";
    case "queue-collect":
    case "queue-steer":
      return "";
  }
}

/**
 * Position the `+` popover as `position: fixed` above the trigger
 * button using the trigger's viewport rect. The dock is nested
 * inside several `overflow: hidden` ancestors (chat-shell,
 * chat-layout__main, chat-compose__shell). A normal
 * `position: absolute; bottom: calc(100% + 6px)` is clipped by
 * those ancestors whenever the panel floats past their top edge.
 * Switching to `position: fixed` with explicit viewport
 * coordinates lets the panel escape every ancestor clip and
 * render directly above the trigger regardless of nesting.
 */
function positionPanelFixed(panel: HTMLElement, trigger: HTMLElement): void {
  const GAP = 6;
  const rect = trigger.getBoundingClientRect();
  // Render at the trigger's top edge, then translate up by the
  // panel's own height so the panel sits ABOVE the trigger.
  panel.style.position = "fixed";
  panel.style.left = `${rect.left}px`;
  panel.style.top = `${rect.top - GAP}px`;
  panel.style.transform = "translateY(-100%)";
  panel.style.bottom = "auto";
  panel.style.right = "auto";
  // Stay above every ancestor clip layer.
  panel.style.zIndex = "2147483647";
}

export function renderChatComposerPlusMenu(props: ChatComposerPlusMenuProps): TemplateResult {
  const queueMode = props.queueMode;
  const triggerRef: Ref<HTMLButtonElement> = createRef();
  const panelRef: Ref<HTMLDivElement> = createRef();
  // Reposition the panel on every render so resize / scroll /
  // layout shifts don't leave it hovering in the wrong place.
  // We also reposition on the next animation frame to capture
  // the trigger's rect AFTER any parent flex reflow.
  const repositionPanel = () => {
    const panel = panelRef.value;
    const trigger = triggerRef.value;
    if (panel && trigger && props.open) {
      positionPanelFixed(panel, trigger);
    }
  };

  const onTriggerKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      props.onToggle();
      props.onFocusChange(0);
    }
  };

  const onPanelKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = props.focusedIndex < 0 ? 0 : Math.min(props.focusedIndex + 1, ROWS.length - 1);
      props.onFocusChange(next);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = props.focusedIndex < 0 ? ROWS.length - 1 : Math.max(props.focusedIndex - 1, 0);
      props.onFocusChange(next);
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      props.onFocusChange(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      props.onFocusChange(ROWS.length - 1);
      return;
    }
  };

  return html`
    <div class="chat-compose__plus-wrap">
      <button
        type="button"
        class="chat-compose__plus-btn"
        data-testid="chat-composer-plus"
        title="More composer actions"
        aria-label="More composer actions"
        aria-haspopup="menu"
        aria-expanded=${props.open ? "true" : "false"}
        ?disabled=${!props.open && (props.sessionKey == null || props.sessionKey === "")}
        ${ref(triggerRef)}
        @click=${() => props.onToggle()}
        @keydown=${onTriggerKey}
      >
        ${icons.plus}
      </button>
      ${
        props.open
          ? html`
              <div
                class="chat-compose__plus-panel"
                data-testid="chat-composer-plus-panel"
                role="menu"
                aria-label="Composer actions"
                ${ref(panelRef)}
                @keydown=${onPanelKey}
                @click=${(e: MouseEvent) => e.stopPropagation()}
                ${ref((el) => {
                  if (el) {
                    // After Lit commits the panel into the DOM,
                    // schedule a position update so the
                    // trigger's rect is measured AFTER the
                    // flex / layout shift that opened us.
                    requestAnimationFrame(() => {
                      repositionPanel();
                    });
                  }
                })}
              >
                ${ROWS.map((row, idx) => renderRow(row, idx, props))}
                <div class="chat-compose__plus-divider" aria-hidden="true"></div>
                <div class="chat-compose__plus-queue" role="group" aria-label="Queue mode">
                  <button
                    type="button"
                    class="chat-compose__plus-queue-option ${
                      queueMode === "collect" ? "chat-compose__plus-queue-option--active" : ""
                    }"
                    data-testid="chat-composer-plus-collect"
                    title=${rowTooltip("queue-collect")}
                    aria-pressed=${queueMode === "collect" ? "true" : "false"}
                    @click=${() => {
                      if (queueMode !== "collect") {
                        props.onQueueModeToggle("collect");
                      }
                      props.onClose();
                    }}
                  >
                    <span class="chat-compose__plus-queue-label">Collect</span>
                  </button>
                  <button
                    type="button"
                    class="chat-compose__plus-queue-option ${
                      queueMode === "steer" ? "chat-compose__plus-queue-option--active" : ""
                    }"
                    data-testid="chat-composer-plus-steer"
                    title=${rowTooltip("queue-steer")}
                    aria-pressed=${queueMode === "steer" ? "true" : "false"}
                    @click=${() => {
                      if (queueMode !== "steer") {
                        props.onQueueModeToggle("steer");
                      }
                      props.onClose();
                    }}
                  >
                    <span class="chat-compose__plus-queue-label">Steer</span>
                  </button>
                </div>
              </div>
            `
          : nothing
      }
    </div>
  `;
}

function renderRow(row: PlusMenuRow, idx: number, props: ChatComposerPlusMenuProps): TemplateResult {
  const focused = props.focusedIndex === idx;

  const onActivate = () => {
    switch (row) {
      case "palace":
        props.onOpenPalace();
        break;
      case "draft":
        props.onOpenDraft();
        break;
      case "controls":
        props.onOpenControls();
        break;
      case "mention":
        props.onOpenMention();
        break;
      case "commands":
        props.onOpenCommands();
        break;
    }
    props.onClose();
  };

  const onRowKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  };

  return html`
    <button
      type="button"
      class="chat-compose__plus-row ${focused ? "chat-compose__plus-row--focused" : ""}"
      role="menuitem"
      data-testid=${`chat-composer-plus-${row}`}
      title=${rowTooltip(row)}
      tabindex=${focused ? "0" : "-1"}
      @mouseenter=${() => props.onFocusChange(idx)}
      @click=${onActivate}
      @keydown=${onRowKey}
    >
      <span class="chat-compose__plus-row-icon" aria-hidden="true">${iconForRow(row)}</span>
      <span class="chat-compose__plus-row-label">${rowLabel(row)}</span>
      <span class="chat-compose__plus-row-chevron" aria-hidden="true">›</span>
    </button>
  `;
}
